import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import { jsx as _jsx } from "react/jsx-runtime";
import { useApolloClient } from '@apollo/react-hooks';
import Auth from '@aws-amplify/auth';
import { StorageHelper } from '@aws-amplify/core';
import * as React from 'react';
import { useAsyncFn, usePrevious } from 'react-use';
import { AppName, createLogger } from '../logging';
import { createContext } from '../react';
import session from '../session';
const logger = createLogger(AppName.Common, 'auth');
const OAUTH_PROVIDER_PREFIXES = ['Google_', 'Facebook_', 'Apple_'];

const isOAuthUser = username => OAUTH_PROVIDER_PREFIXES.some(prefix => username === null || username === void 0 ? void 0 : username.startsWith(prefix));

const [useAuth, AuthContext] = createContext();

const useAuthenticated = () => {
  const {
    authenticated
  } = useAuth();
  return authenticated;
};

const useUser = () => {
  const {
    user
  } = useAuth();
  return user;
};

const AuthConsumer = AuthContext.Consumer;

const useUserChange = (user, callback) => {
  const prevUser = usePrevious(user);
  const callbackRef = React.useRef(callback);
  React.useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);
  React.useEffect(() => {
    if (callbackRef.current) {
      callbackRef.current(user, prevUser);
    }
  }, [prevUser, user]);
};

const AuthProvider = ({
  children,
  onUserChange,
  FallbackComponent
}) => {
  const client = useApolloClient();
  session.config();
  const [verifyState, verify] = useAsyncFn(function () {
    var _ref = _asyncToGenerator(function* (params = {
      bypassCache: true
    }) {
      try {
        var _cognitoUser$signInUs, _cognitoUser$signInUs2;

        const cognitoUser = yield Auth.currentAuthenticatedUser(params);
        const idTokenPayload = (_cognitoUser$signInUs = cognitoUser.signInUserSession) === null || _cognitoUser$signInUs === void 0 ? void 0 : (_cognitoUser$signInUs2 = _cognitoUser$signInUs.idToken) === null || _cognitoUser$signInUs2 === void 0 ? void 0 : _cognitoUser$signInUs2.payload;
        const normalizedUser = { ...cognitoUser,
          attributes: cognitoUser.attributes && Object.keys(cognitoUser.attributes).length > 0 ? cognitoUser.attributes : {
            email: idTokenPayload === null || idTokenPayload === void 0 ? void 0 : idTokenPayload.email,
            phone_number: idTokenPayload === null || idTokenPayload === void 0 ? void 0 : idTokenPayload.phone_number,
            email_verified: idTokenPayload === null || idTokenPayload === void 0 ? void 0 : idTokenPayload.email_verified,
            phone_number_verified: idTokenPayload === null || idTokenPayload === void 0 ? void 0 : idTokenPayload.phone_number_verified,
            sub: idTokenPayload === null || idTokenPayload === void 0 ? void 0 : idTokenPayload.sub
          }
        };
        return normalizedUser;
      } catch {
        yield session.verify();
        return undefined;
      }
    });

    return function () {
      return _ref.apply(this, arguments);
    };
  }());
  const [changePasswordState, changePassword] = useAsyncFn(function () {
    var _ref2 = _asyncToGenerator(function* (oldPassword, newPassword) {
      if (!oldPassword || !newPassword) throw new Error();
      const user = yield Auth.currentAuthenticatedUser({
        bypassCache: true
      });
      yield Auth.changePassword(user, oldPassword, newPassword);
    });

    return function (_x, _x2) {
      return _ref2.apply(this, arguments);
    };
  }());
  const [updatePasswordAttributesState, updatePasswordAttributes] = useAsyncFn(_asyncToGenerator(function* () {
    const user = yield Auth.currentAuthenticatedUser({
      bypassCache: true
    });
    yield Auth.updateUserAttributes(user, {
      'custom:password_update_date': new Date().toISOString(),
      'custom:force_reset_password': 'False'
    });
  }));
  const [initialized, setInitialized] = React.useState(false);
  const [updatingAuthState, setUpdatingAuthState] = React.useState(false);
  const user = React.useMemo(() => verifyState.value, [verifyState]);
  useUserChange(user, onUserChange);
  React.useEffect(() => {
    verify({
      bypassCache: true
    }).then(() => setInitialized(true));
  }, [verify]);
  const value = React.useMemo(() => {
    const signOut = function () {
      var _ref4 = _asyncToGenerator(function* () {
        if (user) {
          setUpdatingAuthState(true);

          if (isOAuthUser(user.username)) {
            try {
              const storage = new StorageHelper().getStorage();
              Object.keys(storage).forEach(key => {
                if (typeof key === 'string' && key.startsWith('CognitoIdentityServiceProvider')) {
                  storage.removeItem(key);
                }
              });
            } catch (error) {
              logger.log('Storage cleanup error (non-critical):', error);
            }
          } else {
            try {
              yield Auth.signOut({
                global: false
              });
            } catch (error) {
              logger.log('Sign out error (non-critical):', error);
            }
          }

          yield verify({
            bypassCache: true
          });
          client.stop();
          yield client.resetStore();
          setUpdatingAuthState(false);
        }
      });

      return function signOut() {
        return _ref4.apply(this, arguments);
      };
    }();

    const userObj = user === undefined ? {
      user: undefined,
      authenticated: false
    } : {
      user,
      authenticated: true
    };
    return { ...userObj,
      verifyState,
      verify,
      signOut,
      changePasswordState,
      changePassword,
      updatePasswordAttributesState,
      updatePasswordAttributes,
      updatingAuthState
    };
  }, [user, verifyState, verify, changePasswordState, changePassword, updatePasswordAttributesState, updatePasswordAttributes, updatingAuthState, client]);

  if (!initialized) {
    if (FallbackComponent) {
      return _jsx(FallbackComponent, {});
    }

    return null;
  }

  return _jsx(AuthContext.Provider, {
    value: value,
    children: children
  });
};

const getToken = function () {
  var _ref5 = _asyncToGenerator(function* () {
    try {
      const currentSession = yield Auth.currentSession();
      const token = currentSession.getIdToken().getJwtToken();
      return token;
    } catch {
      return undefined;
    }
  });

  return function getToken() {
    return _ref5.apply(this, arguments);
  };
}();

export { Auth, AuthConsumer, AuthContext, AuthProvider, getToken, isOAuthUser, useAuth, useAuthenticated, useUser };